import {
  endOfDay,
  endOfMonth,
  endOfQuarter,
  endOfWeek,
  endOfYear,
  startOfDay,
  startOfMonth,
  startOfQuarter,
  startOfWeek,
  startOfYear,
  subDays,
  subMonths,
  subQuarters,
  subWeeks,
  subYears,
} from "date-fns"

/**
 * Reporting period presets — the standard, domain-agnostic date-range vocabulary for reporting/analytics
 * across the app. "To date" presets always end at the current day (e.g. Month to Date = first day of the
 * current month → today). This utility owns the range math so no screen re-implements start/endOf logic.
 *
 * Fiscal-period presets ({@link PeriodPreset.CurrentFiscalPeriod} / {@link PeriodPreset.PreviousFiscalPeriod})
 * are domain data: the caller supplies the fiscal bounds via {@link ResolvePeriodOptions.fiscalPeriods};
 * this file never fetches or hardcodes finance-specific data.
 *
 * {@link PeriodPreset.Custom} is the caller-supplied range. `resolvePeriodRange` returns null for it by
 * design — there is nothing to compute, the dates come from the user — so a consumer that offers Custom must
 * read its own two date fields instead.
 */
export enum PeriodPreset {
  Today = "today",
  Yesterday = "yesterday",
  WeekToDate = "week-to-date",
  ThisWeek = "this-week",
  LastWeek = "last-week",
  MonthToDate = "month-to-date",
  ThisMonth = "this-month",
  LastMonth = "last-month",
  QuarterToDate = "quarter-to-date",
  ThisQuarter = "this-quarter",
  LastQuarter = "last-quarter",
  YearToDate = "year-to-date",
  ThisYear = "this-year",
  LastYear = "last-year",
  // Rolling windows ending today (used by member/people analytics).
  LastSixMonths = "last-six-months",
  LastTwelveMonths = "last-twelve-months",
  CurrentFiscalPeriod = "current-fiscal-period",
  PreviousFiscalPeriod = "previous-fiscal-period",
  /** Two dates the user typed. Has no range math of its own — see the note above. */
  Custom = "custom",
  /**
   * Everything ever recorded — no lower bound and no upper bound.
   *
   * Like {@link Custom} it resolves to `null`, because there is no range to compute. The two mean OPPOSITE
   * things to a caller — Custom says "read the user's two dates", AllTime says "apply no date filter at all"
   * — so never branch on `null` alone. Use {@link isUnboundedPreset} to tell them apart.
   */
  AllTime = "all-time",
}

export type PeriodRange = { start: Date; end: Date }

/** A fiscal period's absolute bounds, supplied by the caller for the fiscal presets. */
export type FiscalPeriodBound = { start: Date; end: Date }

export type ResolvePeriodOptions = {
  /** "Now" reference. Callers computing in a specific timezone should pass a wall-clock date for that zone. */
  now: Date
  /** 0 = Sunday … 6 = Saturday. Defaults to Monday to match the finance date-range contract. */
  weekStartsOn?: 0 | 1 | 2 | 3 | 4 | 5 | 6
  /** Fiscal-period bounds (unordered ok) — required to resolve the fiscal presets. */
  fiscalPeriods?: FiscalPeriodBound[]
}

export const FISCAL_PERIOD_PRESETS: readonly PeriodPreset[] = [
  PeriodPreset.CurrentFiscalPeriod,
  PeriodPreset.PreviousFiscalPeriod,
]

export function isFiscalPeriodPreset(preset: PeriodPreset): boolean {
  return FISCAL_PERIOD_PRESETS.includes(preset)
}

/**
 * Presets that deliberately have NO bounds.
 *
 * `resolvePeriodRange` returns `null` for three different reasons — a custom range the caller must read from
 * its own fields, a fiscal preset with no bounds supplied, and this. Only this one means "query everything",
 * so it needs its own test rather than a bare null check.
 */
export const UNBOUNDED_PRESETS: readonly PeriodPreset[] = [PeriodPreset.AllTime]

export function isUnboundedPreset(preset: PeriodPreset): boolean {
  return UNBOUNDED_PRESETS.includes(preset)
}

/** Ordered, grouped preset layout for a picker (day / week / month / quarter / year / fiscal). */
export const PERIOD_PRESET_GROUPS: ReadonlyArray<{ readonly presets: readonly PeriodPreset[] }> = [
  { presets: [PeriodPreset.Today, PeriodPreset.Yesterday] },
  { presets: [PeriodPreset.WeekToDate, PeriodPreset.ThisWeek, PeriodPreset.LastWeek] },
  { presets: [PeriodPreset.MonthToDate, PeriodPreset.ThisMonth, PeriodPreset.LastMonth] },
  { presets: [PeriodPreset.QuarterToDate, PeriodPreset.ThisQuarter, PeriodPreset.LastQuarter] },
  { presets: [PeriodPreset.YearToDate, PeriodPreset.ThisYear, PeriodPreset.LastYear] },
  { presets: [PeriodPreset.CurrentFiscalPeriod, PeriodPreset.PreviousFiscalPeriod] },
]

function resolveFiscalRange(
  preset: PeriodPreset,
  now: Date,
  fiscalPeriods: FiscalPeriodBound[] | undefined,
): PeriodRange | null {
  if (!fiscalPeriods?.length) return null
  const sorted = [...fiscalPeriods].sort((left, right) => left.start.getTime() - right.start.getTime())
  const currentIndex = sorted.findIndex(
    (period) => now.getTime() >= period.start.getTime() && now.getTime() <= period.end.getTime(),
  )
  if (currentIndex === -1) return null
  if (preset === PeriodPreset.CurrentFiscalPeriod) return sorted[currentIndex]
  return sorted[currentIndex - 1] ?? null
}

/**
 * Resolve a preset to a concrete `{ start, end }` range. Returns `null` when the preset cannot be resolved
 * (a fiscal preset without matching fiscal bounds), so callers can treat it as "no range".
 */
export function resolvePeriodRange(preset: PeriodPreset, options: ResolvePeriodOptions): PeriodRange | null {
  const { now, weekStartsOn = 1, fiscalPeriods } = options
  const weekOptions = { weekStartsOn }

  switch (preset) {
    case PeriodPreset.Today:
      return { start: startOfDay(now), end: endOfDay(now) }
    case PeriodPreset.Yesterday: {
      const yesterday = subDays(now, 1)
      return { start: startOfDay(yesterday), end: endOfDay(yesterday) }
    }
    case PeriodPreset.WeekToDate:
      return { start: startOfWeek(now, weekOptions), end: endOfDay(now) }
    case PeriodPreset.ThisWeek:
      return { start: startOfWeek(now, weekOptions), end: endOfWeek(now, weekOptions) }
    case PeriodPreset.LastWeek: {
      const lastWeek = subWeeks(now, 1)
      return { start: startOfWeek(lastWeek, weekOptions), end: endOfWeek(lastWeek, weekOptions) }
    }
    case PeriodPreset.MonthToDate:
      return { start: startOfMonth(now), end: endOfDay(now) }
    case PeriodPreset.ThisMonth:
      return { start: startOfMonth(now), end: endOfMonth(now) }
    case PeriodPreset.LastMonth: {
      const lastMonth = subMonths(now, 1)
      return { start: startOfMonth(lastMonth), end: endOfMonth(lastMonth) }
    }
    case PeriodPreset.QuarterToDate:
      return { start: startOfQuarter(now), end: endOfDay(now) }
    case PeriodPreset.ThisQuarter:
      return { start: startOfQuarter(now), end: endOfQuarter(now) }
    case PeriodPreset.LastQuarter: {
      const lastQuarter = subQuarters(now, 1)
      return { start: startOfQuarter(lastQuarter), end: endOfQuarter(lastQuarter) }
    }
    case PeriodPreset.YearToDate:
      return { start: startOfYear(now), end: endOfDay(now) }
    case PeriodPreset.ThisYear:
      return { start: startOfYear(now), end: endOfYear(now) }
    case PeriodPreset.LastYear: {
      const lastYear = subYears(now, 1)
      return { start: startOfYear(lastYear), end: endOfYear(lastYear) }
    }
    case PeriodPreset.LastSixMonths:
      return { start: startOfDay(subMonths(now, 6)), end: endOfDay(now) }
    case PeriodPreset.LastTwelveMonths:
      return { start: startOfDay(subMonths(now, 12)), end: endOfDay(now) }
    case PeriodPreset.CurrentFiscalPeriod:
    case PeriodPreset.PreviousFiscalPeriod:
      return resolveFiscalRange(preset, now, fiscalPeriods)
    // Explicit, not falling through to `default`: an unbounded period has no range BY DESIGN, and saying so
    // here keeps it distinguishable from "this preset is unknown to me".
    case PeriodPreset.AllTime:
      return null
    default:
      return null
  }
}
